import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { all_routes } from '../../router/all_routes';
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from '../../../core/common/collapse-header/collapse-header';
import { API } from '../../../common/Constants';
import { getAPIHeaders, getFormattedDateDDMMYYYY } from '../../../common/Utils';
import useFetch from '../../../hooks/useFetch';
import { Leave } from '../../../types/dataModel';
import PreLoader from '../../../core/common/preLoader';
import DeleteModal from '../../../core/modals/deleteModal';
import SuccessModal from '../../../core/modals/successModal';
import CopyRightInfo from '../../../core/common/copyRightInfo';
import { useAccess } from '../../../hooks/accessContext';
import DropDown from '../../../core/common/commonDropDown';
import PredefinedDateRanges from '../../../core/common/datePicker';
import CustomToast from '../../../core/common/customToast';
import moment from 'moment';

const Attendance = () => {
  const  { hasAttendEdit } = useAccess()
  const { isLoading, callFetch } = useFetch();
  const [classes, setClasses] = useState<any[]>([]);
  const [selectedClass, setSelectedClass] = useState<number>();
  const [periods, setPeriods] = useState<any[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<number>(0);
  const [selectedDateRange, setSelectedDateRange] = useState<{ start: moment.Moment, end: moment.Moment }>({
          start: moment().startOf('year'),
          end: moment().endOf('year'),
  });
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL');
  const [attendance, setAttendance] = useState<Leave[]>();
  const [attendanceBatchStats, setAttendanceBatchStats] = useState<any>({
    totalBatches: 0,
    totalSessions: 0,
    averageAttendanceRate: 0,
    totalStudents: 0,
  });

  const [editItem, setEditItem] = useState<any>({
            id:undefined, 
            message: undefined,
  })
  const [toastSettings, setToastSettings] = useState<{
          show: boolean;
          message: string;
          heading: string;
          type: "ERROR" | "SUCCESS" | "INFO";
      }>({
          show: false,
          message: "Something went wrong please try again later",
          heading: "Oops! Something went wrong",
          type: "ERROR",
  });


  useEffect(() => {
    fetchClasses()
  }, []);

  useEffect(() => {
    if (selectedClass) {

      fetchPeriods();
      fetchStats();
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedClass && selectedDateRange && selectedStatus) {
      fetch();
    }
  }, [selectedClass, selectedPeriod, selectedStatus, selectedDateRange]);

  const fetch = () => {
    let selectedPeriodDetails = periods.find((p) => p.value === selectedPeriod);

    callFetch(API.ATTENDANCE, "POST", getAPIHeaders(),null, {
      curriculumCourseBatchId: selectedClass,
      period: selectedPeriodDetails?.periodNo,
      day: selectedPeriodDetails?.day,
      sessionDateFrom: selectedDateRange?.start?.format("YYYY-MM-DD"),
      sessionDateTo: selectedDateRange?.end?.format("YYYY-MM-DD"),
      attendanceStatuses: selectedStatus === 'ALL' ? ['PRESENT', 'ABSENT'] : [selectedStatus],
    }).then((res) => {
      setAttendance(res?.data || []);
    }).catch((err) => {
      console.error("Error fetching attendance:", err);
      setToastSettings((prev) => ({
        ...prev,
        show: true,
        message: err?.response?.data || "Failed to fetch Attendance",
        heading: "Error",
        type: "ERROR",
      }));
    });
  }

  const fetchStats = () => {
    setAttendanceBatchStats({
      totalBatches: 0,
      totalSessions: 0,
      averageAttendanceRate: 0,
      totalStudents: 0,
    });
    callFetch(API.ATTENDANCE_BATCH_REPORT.replace(":batchId", selectedClass?.toString() || ""),
      "POST", getAPIHeaders(), null, {}).then((res) => {
        setAttendanceBatchStats({
          ...attendanceBatchStats,
          totalSessions: res?.totalSessions || 0,
          averageAttendanceRate: res?.averageAttendanceRate || 0,
          totalStudents: res?.totalStudents || 0,
        });
      }).catch((err) => {
        console.error("Error fetching attendance stats:", err);
        setToastSettings((prev) => ({
          ...prev,
          show: true,
          message: err?.response?.data?.error || err?.response?.data?.error  || "Failed to fetch Attendance Stats",
          heading: "Error",
          type: "ERROR",
        }));
      });
  }


  const fetchClasses = () => {
    callFetch(API.CLASS, "GET", getAPIHeaders(), null, null).then(
      (res) => {
        setClasses(res?.map((item:any) => ({
          label: `${item.name} - ${item.section}`,
          value: item.id,
        })));
        setAttendanceBatchStats({
          ...attendanceBatchStats,
          totalBatches: res?.length || 0,
        });
      }
    ).catch((err) => {
      setToastSettings((prev) => ({
        ...prev,
        show: true,
        message: err?.response?.data || "Failed to fetch Classes",
        heading: "Error",
        type: "ERROR",
      }));
    });
  }

  const fetchPeriods = () => {
    callFetch(API.BATCH_TIMETABLE_PERIODS.replace(":batchId", selectedClass?.toString() || ""),
       "GET", getAPIHeaders(), null, null).then(
      (res) => {
        setPeriods(getPeriodOptions(res));
      }
    ).catch((err) => {
      setToastSettings((prev) => ({
        ...prev,
        show: true,
        message: err?.response?.data || "Failed to fetch Periods",
        heading: "Error",
        type: "ERROR",
      }));
    });
  }


  const update = (status: string) => {
    callFetch(API.ATTENDANCE_UPDATE, "PUT", getAPIHeaders(),null, {
      id: editItem.id,
      attendanceStatus: status,
    }).then((res) => {
      fetch();
    }).catch((err) => {
      console.error("Error marking attendance:", err);
      setToastSettings((prev) => ({
        ...prev,
        show: true,
        message: err?.response?.data || "Failed to mark Attendance",
        heading: "Error",
        type: "ERROR",
      }));
    });
  }

  const columns = [
    {
      title: "Date",
      dataIndex: "sessionDate",
      render: (text: String, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <div className="ms-2">
            <h6 className="fw-medium">
              {record.sessionDate}
            </h6>
            <span className="fs-12 fw-normal ">{record.classDay}</span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => a.sessionDate?.localeCompare(b.sessionDate),
    },
    {
      title: "Batch",
      dataIndex: "batchFullName",
      render: (text: String, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <div className="ms-2">
            <h6 className="fw-medium">
              {record.batchFullName}
            </h6>
            <span className="fs-12 fw-normal ">{record.courseTitle}</span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => a.batchFullName?.localeCompare(b.batchFullName),
    },
    {
      title: "Period",
      dataIndex: "classPeriod",
      render: (text: String, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <div className="ms-2">
            <h6 className="fw-medium">
              {record.subjectTitle}
            </h6>
            <span className="fs-12 fw-normal ">Period: {record.classPeriod}</span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => a.classPeriod - b.classPeriod,
    },
    {
      title: "Student",
      dataIndex: "studentFullName",
      render: (text: String, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          {record.studentProfilePicUrl ? (
            <div className="avatar avatar-md me-2">
              <img 
                src={record.studentProfilePicUrl} 
                alt={`${record.firstName} ${record.lastName}`}
                className="avatar-img rounded-circle"
              />
            </div>
          ) : (
            <div className="avatar avatar-md me-2 bg-light-primary rounded-circle">
              <span className="text-primary fw-bold">
                {record.studentName?.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <div className="ms-2">
            <h6 className="fw-medium">
              {record.studentFullName}
            </h6>
            <span className="fs-12 fw-normal ">Acct# {record.studentAccountId}</span>
            <span className="fs-12 fw-normal "> Roll No# {record.studentRollNumber}</span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => a.studentFullName?.localeCompare(b.studentFullName),
    },
    {
      title: "Status",
      dataIndex: "attendanceStatus",
      render: (text: string) => (
        text === 'PRESENT' ?
          <span className="badge badge-soft-success d-inline-flex align-items-center badge-sm">
              {text}
          </span>
          :
          text === 'ABSENT' ?
          <span className="badge badge-soft-danger d-inline-flex align-items-center badge-sm">
            {text}
          </span>
          :
          <></>
        ),
      sorter: (a: any, b: any) => a.attendanceStatus.localeCompare(b.attendanceStatus),
      defaultSortOrder: 'descend',
    },
    {
      title: "Faculty Assigned",
      dataIndex: "facultyFullName",
      render: (text: String, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          {record.facultyProfilePicUrl ? (
            <div className="avatar avatar-md me-2">
              <img 
                src={record.facultyProfilePicUrl} 
                alt={`${record.facultyFullName}`}
                className="avatar-img rounded-circle"
              />
            </div>
          ) : (
            <div className="avatar avatar-md me-2 bg-light-primary rounded-circle">
              <span className="text-primary fw-bold">
                {record.facultyFullName?.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <div className="ms-2">
            <h6 className="fw-medium">
              {record.facultyFullName}
            </h6>
            <span className="fs-12 fw-normal ">User: {record.facultyUsername}</span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => a.facultyFullName?.localeCompare(b.facultyFullName),
    },
    {
      title: "Marked By",
      dataIndex: "markedByFullName",
      render: (text: String, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <div className="ms-2">
            <h6 className="fw-medium">
              {record.markedByFullName}
            </h6>
            <span className="fs-12 fw-normal ">User: {record.markedByUsername}</span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => a.markedByFullName?.localeCompare(b.markedByFullName),
    },
    {
      title: "Action",
      dataIndex: "actions",
      render: (text:string, record:any) => (
        <div className="action-icon d-inline-flex">
          <button
            hidden={!hasAttendEdit() || record.attendanceStatus === 'PRESENT'}
            className="me-2 btn btn-success btn-sm"
            data-bs-toggle="modal" data-inert={true}
            data-bs-target="#success_modal"
            disabled={record.attendanceStatus === 'PRESENT'}
            onClick={()=>{
              setEditItem({
                id: record?.attendanceId,
                message: `Mark present for ${record?.studentFullName} on ${record.sessionDate}.`
              })
            }}
          >
            Mark Present
          </button>
          <button
            hidden={!hasAttendEdit() || record.attendanceStatus === 'ABSENT'}
            className='btn btn-danger btn-sm'
            data-bs-toggle="modal" data-inert={true}
            data-bs-target="#delete_modal"
            disabled={record.attendanceStatus === 'ABSENT'}
            onClick={()=>{
              setEditItem({
                id: record?.attendanceId,
                message: `Mark absent for ${record?.studentFullName} on ${record.sessionDate}.`
              })
            }}
          >
            Mark Absent
          </button>
        </div>
      ),
    },
  ]

  const renderLeaveStatCards = () => {
    return (          
    <div className="row">
      <div className="col-xl-3 col-md-6">
        <div className="card bg-blue-img">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0 me-2">
                  <span className="avatar avatar-md rounded-circle bg-white d-flex align-items-center justify-content-center">
                    <i className="ti ti-users-group text-info fs-18" />
                  </span>
                </div>
              </div>
              <div className="text-end">
                {
                  selectedClass ?
                  <>
                      <p className="mb-1">Selected Batch</p>
                      <h6>{classes.find((c:any) => selectedClass === c.value)?.label}</h6>
                  </>
                  :
                  <>
                      <p className="mb-1">Total Batches</p>
                      <h4>{attendanceBatchStats.totalBatches}</h4>
                  </>

                }
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="col-xl-3 col-md-6">
        <div className="card bg-green-img">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0 me-2">
                  <span className="avatar avatar-md rounded-circle bg-white d-flex align-items-center justify-content-center">
                    <i className="ti ti-user text-success fs-18" />
                  </span>
                </div>
              </div>
              <div className="text-end">
                <p className="mb-1">Total Students</p>
                <h6>{attendanceBatchStats.totalStudents}</h6>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="col-xl-3 col-md-6">
        <div className="card bg-yellow-img">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0 me-2">
                  <span className="avatar avatar-md rounded-circle bg-white d-flex align-items-center justify-content-center">
                    <i className="ti ti-percentage   text-warning fs-18" />
                  </span>
                </div>
              </div>
              <div className="text-end">
                <p className="mb-1">Attendance Rate</p>
                <h6>{attendanceBatchStats.averageAttendanceRate?.toFixed(2)}</h6>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="col-xl-3 col-md-6">
        <div className="card bg-pink-img">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0 me-2">
                    <span className="avatar avatar-md rounded-circle bg-white d-flex align-items-center justify-content-center">
                    <i className="ti ti-calendar-event text-pink fs-18" />
                    </span>
                </div>
              </div>
              <div className="text-end">
                <p className="mb-1">Total Sessions</p>
                <h6>{attendanceBatchStats.totalSessions}</h6>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>)
  }

  const renderFilters = () => {
    return (
      <div className="d-flex align-items-center gap-1 flex-wrap">
      {/* Batch Name Filter */}
      <div className="d-flex align-items-center">
        <DropDown onChange={(val)=>{setSelectedClass(val)}} 
          defaultValue={''}
          options={classes}
          placeholder="Select Batch"
          value={selectedClass}
        />
      </div>
      <div className="d-flex align-items-center">
        <DropDown onChange={(val)=>{setSelectedPeriod(val)}} 
          defaultValue={0}
          options={periods}
          placeholder="Select Period"
          value={selectedPeriod}
        />
      </div>
      {/* Status Filter */}
      <div className="d-flex align-items-center">
        <DropDown onChange={(val)=>{setSelectedStatus(val)}} 
          defaultValue={'ALL'}
          value={selectedStatus}
          options={[
            {value:'ALL', label:'All Status'},
            {value:'ABSENT', label: 'Absent'}, 
            {value: 'PRESENT', label:'Present'},
          ]}
          placeholder='Select Status'
        />
      </div>
      <div className="d-flex align-items-center">
        <PredefinedDateRanges 
            onChange={(start: moment.Moment, end: moment.Moment)=>{
              setSelectedDateRange({ start, end });
            }}
        />
      </div>
    </div>
    )
  }
  return (
    <>
      <CustomToast
        show={toastSettings?.show}
        message={toastSettings?.message}
        heading={toastSettings?.heading}
        type={toastSettings?.type}
        onClose={() => {
          setToastSettings((prev) => ({
            ...prev,
            show: false,
          }));
        }}
      />
      <PreLoader showLoader={isLoading} />
      <SuccessModal 
        heading="Mark Present"
        message={editItem.message}
        btnText='Yes'
        onConfirm={()=>(update('PRESENT'))}
        renderIcon={()=>(<i className="ti ti-thumb-up fs-36" />)}
      />
      <DeleteModal 
            heading="Mark Absent"
            message={editItem.message}
            btnText='Yes'
            onDelete={()=>(update('ABSENT'))}
            renderIcon={()=>(<i className="ti ti-thumb-down fs-36" />)}
      />
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Attendance</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Employee</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Attendance
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">     
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          {/* Attendance Info */}
          {renderLeaveStatCards()}
          {/* /Attendance Info */}
          {/* Attendance list */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Attendence List</h5>
              {renderFilters()}
            </div>
            <div className="card-body p-0">
              { 
              selectedClass ? 
                <Table dataSource={attendance || []} columns={columns} Selection={false} refresh={fetch} /> 
              :
                <div className="d-flex flex-column align-items-center justify-content-center py-5">
                  <i className="ti ti-info-circle text-primary fs-36 mb-3" />
                  <h4 className="fw-medium mb-1">
                    Select a batch to view attendance
                  </h4>
                  <span className="fs-14 text-muted">
                    Please choose a batch from the dropdown above to see attendance records.
                  </span>
                </div>
              }
            </div>
          </div>
          {/* /Attendance list */}
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
          <CopyRightInfo/>
        </div>
      </div>
      {/* /Page Wrapper */}
    </>


  )
}

function getPeriodOptions(apiData: any) {
  const dayOrder = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  const options: { label: string; value: number, periodNo?: number, day?: string , subject?: string }[] = [];
  const data = apiData.data;
  const dayNames: Record<string, string> = {
    mon: "MONDAY",
    tue: "TUESDAY",
    wed: "WEDNESDAY",
    thu: "THURSDAY",
    fri: "FRIDAY",
    sat: "SATURDAY",
    sun: "SUNDAY",
  };
  if(data){
    options.push({ label: 'ALL', value: 0, periodNo: undefined, day: undefined, subject: undefined });
  }
  dayOrder.forEach(day => {
    if (Array.isArray(data[day])) {
      data[day].forEach((period: any) => {
        options.push({
          label: `${day.charAt(0).toUpperCase() + day.slice(1)}: Period - ${period.periodNo}`,
          value: period.id,
          periodNo: period.periodNo,
          day: dayNames[day] ,
          subject: period.subject,
        });
      });
    }
  });

  return options;
}

export default Attendance